import React, { useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Clock, Check, X, Eye, Download } from 'lucide-react';
import { useVerificationRequests, VerificationRequest } from '@/hooks/admin/useVerificationRequests';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

// Composant ImagePreview (simple placeholder)
const ImagePreview = ({ imageUrl }: { imageUrl?: string }) => {
  if (!imageUrl) return <span>(No image)</span>;
  // TODO: Remplacer par une URL réelle ou une logique pour récupérer l'image depuis le stockage
  return <img src={`/placeholder/${imageUrl}`} alt="Verification Document" className="h-10 w-auto" loading="lazy" />;
};

const VerificationRequestsTab: React.FC = () => {
  const { requests, isLoading, error, updateVerificationStatus } = useVerificationRequests();
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  const handleStatusUpdate = async (requestId: string, status: 'approved' | 'rejected') => {
    setIsUpdating(requestId);
    await updateVerificationStatus(requestId, status);
    setIsUpdating(null);
  };

  const getStatusBadgeVariant = (status: 'pending' | 'approved' | 'rejected'): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'approved':
        return 'default'; // Vert custom?
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return <div>{t('admin.loading_verifications') || 'Loading verification requests...'}</div>;
  }

  if (error) {
    return <div className="text-destructive">{t('admin.error_loading_verifications') || 'Error loading requests:'} {error}</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">{t('admin.verification_requests') || 'Verification Requests'}</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.verifications.user') || 'User'}</TableHead>
            <TableHead>{t('admin.verifications.role') || 'Role'}</TableHead>
            <TableHead>{t('admin.verifications.doc_type') || 'Doc Type'}</TableHead>
            <TableHead>{t('admin.verifications.document') || 'Document'}</TableHead>
            <TableHead>{t('admin.verifications.selfie') || 'Selfie'}</TableHead>
            <TableHead>{t('admin.verifications.submitted_at') || 'Submitted'}</TableHead>
            <TableHead>{t('admin.verifications.status') || 'Status'}</TableHead>
            <TableHead><span className="sr-only">{t('admin.actions') || 'Actions'}</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((req) => (
            <TableRow key={req.id}>
              <TableCell><Checkbox /></TableCell>
              <TableCell>{req.displayName || req.id}</TableCell>
              <TableCell>{req.email}</TableCell>
              <TableCell>{req.documentType || 'N/A'}</TableCell>
              <TableCell><ImagePreview imageUrl={req.documentImage} /></TableCell>
              <TableCell><ImagePreview imageUrl={req.selfieImage} /></TableCell>
              <TableCell>{format(req.submittedAt, 'Pp')}</TableCell>
              <TableCell>
                <Badge variant={getStatusBadgeVariant(req.status)}>{req.status}</Badge>
              </TableCell>
              <TableCell>
                <Dialog> {/* Dialog pour voir les images en grand */} 
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0" disabled={isUpdating === req.id}>
                        <span className="sr-only">{t('admin.open_menu') || 'Open menu'}</span>
                        {isUpdating === req.id ? <Clock className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DialogTrigger asChild>
                        <DropdownMenuItem>
                          <Eye className="mr-2 h-4 w-4" /> {t('admin.actions.view_docs') || 'View Documents'}
                        </DropdownMenuItem>
                      </DialogTrigger>
                      {req.status !== 'approved' && (
                        <DropdownMenuItem onClick={() => handleStatusUpdate(req.id, 'approved')}>
                          <Check className="mr-2 h-4 w-4" /> {t('admin.actions.approve') || 'Approve'}
                        </DropdownMenuItem>
                      )}
                      {req.status !== 'rejected' && (
                        <DropdownMenuItem onClick={() => handleStatusUpdate(req.id, 'rejected')}>
                          <X className="mr-2 h-4 w-4" /> {t('admin.actions.reject') || 'Reject'}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>{t('admin.verifications.details_title') || 'Verification Details for'} {req.displayName || req.id}</DialogTitle>
                      <DialogDescription>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <h3 className="font-semibold mb-2">{t('admin.verifications.document') || 'Document'} ({req.documentType})</h3>
                            {/* Remplacer par l'image réelle */} 
                            <img src={`/placeholder/${req.documentImage}`} alt="Document" className="w-full h-auto rounded" />
                          </div>
                          <div>
                            <h3 className="font-semibold mb-2">{t('admin.verifications.selfie') || 'Selfie'}</h3>
                            <img src={`/placeholder/${req.selfieImage}`} alt="Selfie" className="w-full h-auto rounded" />
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2">
                           {req.status !== 'rejected' && <Button variant="destructive" onClick={() => { handleStatusUpdate(req.id, 'rejected'); /* close dialog */ }}>{t('admin.actions.reject') || 'Reject'}</Button>}
                           {req.status !== 'approved' && <Button onClick={() => { handleStatusUpdate(req.id, 'approved'); /* close dialog */ }}>{t('admin.actions.approve') || 'Approve'}</Button>}
                        </div>
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default VerificationRequestsTab; 